import React, {useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDatabase} from '../context/DatabaseContext';
import {useUserNickname} from '../hooks/useUserNickname';
import {colors} from '../theme/colors';

const DailyValuesScreen = () => {
  const {getEntriesByDate} = useDatabase();
  const location = useLocation();
  const navigate = useNavigate();
  const userNickname = useUserNickname();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
  });

  // Default targets
  const targets = {
    calories: 1500,
    protein: 120,
    carbs: 142,
    fat: 50,
  };

  useEffect(() => {
    if (location.pathname === '/daily-values') {
      loadEntriesForDate(selectedDate);
    }
  }, [location.pathname, selectedDate]);

  const loadEntriesForDate = async (date) => {
    const dateString = date.toISOString().split('T')[0];
    const dateEntries = await getEntriesByDate(dateString);
    setEntries(dateEntries);

    const totalsCalc = dateEntries.reduce(
      (acc, entry) => ({
        calories: acc.calories + entry.calories,
        protein: acc.protein + entry.protein,
        carbs: acc.carbs + entry.carbs,
        fat: acc.fat + entry.fat,
      }),
      {calories: 0, protein: 0, carbs: 0, fat: 0},
    );

    setTotals(totalsCalc);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
    loadEntriesForDate(newDate);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.userName}>{userNickname} ❤️</div>
          <button
            style={styles.backButton}
            onClick={() => navigate('/')}
            aria-label="Go back to home"
            type="button">
            <span aria-hidden="true">←</span>
          </button>
        </div>
        <div style={styles.headerCenter}>
          <div style={styles.profileIcon} aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="18" stroke={colors.primary} strokeWidth="2" fill="none"/>
              <path d="M12 16 Q20 8 28 16" stroke={colors.primary} strokeWidth="2" fill="none"/>
              <circle cx="20" cy="20" r="3" fill={colors.primary}/>
            </svg>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.bellIcon} aria-hidden="true">🔔</div>
        </div>
      </div>

      {/* Title */}
      <div style={styles.titleSection}>
        <div style={styles.title}>ערכים יומיים</div>
      </div>

      {/* Date Navigation */}
      <div style={styles.dateNavigation}>
        <button
          style={styles.dateNavButton}
          onClick={() => navigateDate('prev')}
          aria-label="Previous day"
          type="button">
          <span aria-hidden="true">←</span>
        </button>
        <div style={styles.dateText}>{formatDate(selectedDate)}</div>
        <button
          style={styles.dateNavButton}
          onClick={() => navigateDate('next')}
          aria-label="Next day"
          type="button">
          <span aria-hidden="true">→</span>
        </button>
      </div>

      {/* Values Grid */}
      <div style={styles.valuesGrid}>
        <div style={styles.valueBox}>
          <div style={styles.valueNumber}>
            {totals.protein.toFixed(2)}/{targets.protein}
          </div>
          <div style={styles.valueLabel}>סך גרם חלבון</div>
        </div>

        <div style={styles.valueBox}>
          <div style={styles.valueNumber}>
            {totals.calories.toFixed(2)}/{targets.calories}
          </div>
          <div style={styles.valueLabel}>סך קלוריות</div>
        </div>

        <div style={styles.valueBox}>
          <div style={styles.valueNumber}>
            {totals.fat.toFixed(2)}/{targets.fat}
          </div>
          <div style={styles.valueLabel}>סך גרם שומן</div>
        </div>

        <div style={styles.valueBox}>
          <div style={styles.valueNumber}>
            {totals.carbs.toFixed(2)}/{targets.carbs}
          </div>
          <div style={styles.valueLabel}>סך גרם פחמימה</div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
    paddingBottom: 20,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    paddingTop: '20px',
  },
  headerLeft: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  userName: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: 'bold',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: colors.textPrimary,
    fontSize: 24,
    cursor: 'pointer',
    padding: 8,
  },
  headerCenter: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  profileIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: colors.surface,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `2px solid ${colors.primary}`,
  },
  headerRight: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  bellIcon: {
    fontSize: 24,
    color: colors.textPrimary,
  },
  titleSection: {
    padding: '20px',
    paddingTop: '10px',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: colors.textPrimary,
    textAlign: 'center',
  },
  dateNavigation: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 20px',
    gap: 12,
  },
  dateNavButton: {
    background: 'none',
    border: 'none',
    color: colors.primary,
    fontSize: 20,
    cursor: 'pointer',
    padding: 8,
  },
  dateText: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: 500,
  },
  valuesGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    padding: '20px',
  },
  valueBox: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${colors.border}`,
    minHeight: 120,
  },
  valueNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 12,
    textAlign: 'center',
  },
  valueLabel: {
    fontSize: 14,
    color: colors.textPrimary,
    textAlign: 'center',
    fontWeight: 500,
  },
};

export default DailyValuesScreen;

